import React, { useEffect, useRef } from 'react';
import { Box, Paper, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

/**
 * Phone and desktop previews of the live render. In edit mode the render marks every
 * authored block; a click inside the iframe posts {type: 'website-edit-field', field} and
 * the editor panel focuses that field (no in-place editing: explicit Save/Cancel lives in the
 * panel, per the dialog convention).
 */
export default function SitePreview({ previewUrl, editMode, device, onDeviceChange, onFieldClick, reloadKey }) {
  const { t } = useTranslation();
  const frameRef = useRef(null);

  useEffect(() => {
    const handler = (event) => {
      if (event?.data?.type === 'website-edit-field' && event.data.field) {
        onFieldClick?.(event.data.field);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [onFieldClick]);

  const src = previewUrl ? `${previewUrl}${editMode ? '?edit=1' : ''}${reloadKey ? `${editMode ? '&' : '?'}r=${reloadKey}` : ''}` : '';
  const isPhone = device === 'phone';

  return (
    <Box data-testid="website-preview">
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">{t('website.preview_title')}</Typography>
        <ToggleButtonGroup size="small" exclusive value={device} onChange={(e, v) => v && onDeviceChange(v)}>
          <ToggleButton value="phone" data-testid="preview-phone">{t('website.preview_phone')}</ToggleButton>
          <ToggleButton value="desktop" data-testid="preview-desktop">{t('website.preview_desktop')}</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Paper
        variant="outlined"
        sx={{
          mx: 'auto',
          width: isPhone ? 390 : '100%',
          maxWidth: '100%',
          height: isPhone ? 720 : 760,
          overflow: 'hidden',
          borderRadius: isPhone ? '28px' : 2,
          bgcolor: '#fff',
        }}
      >
        {src ? (
          <iframe
            ref={frameRef}
            title="site-preview"
            src={src}
            style={{ width: '100%', height: '100%', border: 0 }}
            data-testid="preview-iframe"
          />
        ) : (
          <Box sx={{ p: 3 }}>
            <Typography color="text.secondary">{t('website.preview_pending')}</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
